import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppWindow, Bell, Check, ChevronDown, Columns2, ExternalLink, Eye, EyeOff, KeyRound, LogOut, Menu as IconeMenu, Moon, PanelLeftClose, PanelLeftOpen, Settings, Settings2, Sun, UserRound, UsersRound, X } from 'lucide-react';
import { urlEmbutivel } from '@malgoos/shared';
import { MENUS } from '../nucleo/menus';
import { MarcaDoMenu } from '../componentes/marca';
import { destinoDoMenu, modulosWeb, paginasDoSubsistema } from '../nucleo/modulos-web';
import { linkDoCofre, primeiroDestino } from './casca-nova';
import { comSubsistema, filtroDoSubsistema, ICONES_SUBSISTEMAS, ProvedorSubsistemaDoPainel, useSubsistema } from '../nucleo/subsistemas';
import { useSessao } from '../nucleo/sessao';
import { ProvedorAcaoPrimaria } from '../componentes/acao-primaria';
import { useEsc, useFocoInicial } from '../componentes/sobreposicao';
import { LimiteErro } from '../componentes/limite-erro';
import { Tour } from '../componentes/tour';
import { Menu, useCamadaFlutuante, type ItemMenu } from '../componentes/menu';
import { useToast } from '../componentes/toast';
import { ATALHO_DISCRETO, ModalPreferenciasDiscreto, VigiaDiscreto } from '../componentes/discreto';
import { useDiscreto } from '../componentes/sensivel';
import { alternarDiscreto } from '../nucleo/discreto';
import { anunciarOcultos } from '../componentes/sensivel';
import { email, type Idioma } from '../nucleo/formato';
import { mensagemDeErro } from '../nucleo/api';
import { itensSeletorEmpresa, SeletorEmpresa } from '../nucleo/empresa/SeletorEmpresa';
import { agruparWorkspaces, mostrarWorkspaces } from './workspaces-perfil';
import { CAMINHO_NOTIFICACOES, PopupNotificacoes, SeloNotificacoes, VigiaNotificacoes } from '../nucleo/notificacoes/Sino';
import { caminhoExterno, useCelular } from '../paginas/Externo';
import { AvatarUsuario } from '../componentes/avatar-usuario';
import { BuscaGlobal } from './barra/BuscaGlobal';
import { BotaoMalgoIA } from './barra/MalgoIA';
import { BotaoFlutuanteCriar } from '../nucleo/criacao-rapida';
import { FaixaAmbiente } from './FaixaAmbiente';
import { ModalSimularUsuario, podeSimular } from './simular-usuario';
import { useMenuRecolhido } from './menu-lateral';
import { PularParaConteudo } from '../componentes/pular-conteudo';
import { AreaDoConteudo, ProvedorTelaDividida, useEstadoTelaDividida, useMenuDaDivisao, useTelaDividida } from './tela-dividida';
import { useAcessoNavegacao, useModulosDoMenu } from './Shell';

/**
 * CRM no novo desenho, Fase 1 (ficha `demandas/entrada/2026-10-26_malgo_MOD_crm-novo-desenho.md`, N2 a N6): a casca do clone do
 * RD (`~/Documents/clone-rd-sandbox`) nos workspaces com a chave `novoDesenho` ligada. Barra superior na superfície da marca
 * com o seletor de sub-sistema (no lugar da tela `/subsistemas`), atalho, Cofre, Notificações, Configurações, perfil (Minha
 * conta, workspace com o padrão do login, empresa e idioma); o Sair fica na barra (lista de 30/10, item 4); barra lateral só com as páginas do sub-sistema, ícone e
 * nome, e no rodapé o modo escuro e o recolher. O conteúdo de cada página segue o próprio padrão.
 */

const IDIOMAS: Idioma[] = ['pt', 'en', 'es'];
const iniciais = (nome: string) =>
  nome
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** N5: seletor de sub-sistema do topo (ADM, CRM, ERP, RH, Suporte, Automações e os links externos). */
function SeletorSubsistema() {
  const { t } = useTranslation();
  const sub = useSubsistema();
  const s = useSessao();
  const navegarJanela = useNavigate();
  const d = useTelaDividida();
  // Lista de 30/10 B (tela dividida): com o painel 2 ativo, o seletor é o dele (o `sub` vem do provedor do painel) e a troca
  // navega no roteador dele; o painel 1 não muda.
  const navegar = (para: string) => (d?.ativo && d.foco === 'lado' ? d.navegarNoLado(para) : navegarJanela(para));
  const desktop = !useCelular();
  if (!sub.opcoes.length && !sub.links.length) return null;
  const nome = sub.atual ? t(`subsistemas.nomes.${sub.atual}`) : t('subsistemas.escolher');
  const itens: ItemMenu[] = [
    ...sub.opcoes.map((codigo) => {
      const Icone = ICONES_SUBSISTEMAS[codigo];
      return {
        chave: codigo,
        rotulo: t(`subsistemas.nomes.${codigo}`),
        icone: Icone ? <Icone className="size-4 shrink-0" aria-hidden /> : undefined,
        atual: codigo === sub.atual,
        aoClicar: () => {
          if (codigo === sub.atual) return;
          sub.escolher(codigo);
          navegar(primeiroDestino(s, filtroDoSubsistema(sub.config, codigo), codigo, sub.config));
        },
      };
    }),
    ...sub.links.map((l) => {
      const embutido = desktop && !!l.embutir && urlEmbutivel(l.url);
      return {
        chave: `link:${l.codigo}`,
        rotulo: embutido ? l.nome : `${l.nome} ${t('subsistemas.novaAba')}`,
        icone: embutido ? <AppWindow className="size-4 shrink-0" aria-hidden /> : <ExternalLink className="size-4 shrink-0" aria-hidden />,
        aoClicar: () => (embutido ? navegar(caminhoExterno(l.codigo)) : void window.open(l.url, '_blank', 'noopener,noreferrer')),
      };
    }),
  ];
  return (
    // Rodada rl30-2 (VIS-M1): o seletor encolhe e o nome do sub-sistema corta (Automações, Suporte), para o Sair caber a 360 px.
    <div data-seletor-subsistema="" data-tour="subsistema" className="min-w-0 shrink">
      <Menu
        rotulo={sub.atual ? t('casca.subsistema', { nome }) : t('casca.trocarSubsistema')}
        itens={itens}
        classePainel="left-0 w-60"
        classeGatilho="flex h-10 max-w-full min-w-0 items-center gap-0.5 rounded-lg px-1 text-base font-semibold text-tinta-1 hover:bg-sup-2 sm:gap-1.5 sm:px-2"
        gatilho={
          <>
            <span className="truncate" title={nome}>
              {nome}
            </span>
            {/* Rodada rl30-3 (VIS-M1): no celular a seta sai e o botão aperta, para o nome (CRM, ADM, RH) caber inteiro a 360 px. */}
            <ChevronDown className="hidden size-4 shrink-0 text-tinta-3 sm:block" aria-hidden />
          </>
        }
      />
    </div>
  );
}

/** Botão de ícone da barra superior (mesmo desenho para link e botão). */
const CLASSE_ICONE_FORMA = 'relative flex size-10 shrink-0 items-center justify-center rounded-lg';
const CLASSE_ICONE = `${CLASSE_ICONE_FORMA} text-tinta-2 hover:bg-sup-2 hover:text-tinta-1`;
/**
 * Review rlb-1 (VIS-B2): o botão da tela dividida ligado sem a cor cinza da classe de sempre (que vinha depois no CSS e
 * ganhava do acento) e com o contorno de acento: diferente do hover (só o fundo) e com contraste de 3:1 contra a barra.
 */
const CLASSE_ICONE_LIGADO = `${CLASSE_ICONE_FORMA} bg-sup-2 text-acento-forte ring-1 ring-inset ring-acento-forte`;

/**
 * N2 e N3: o perfil (clique no nome). Minha conta, workspace (trocar e o padrão do login, N1), empresa (grupo com várias),
 * idioma (o Sair saiu daqui: lista de 30/10, item 4). Painel não modal: Esc e clique fora fecham, o foco volta ao botão.
 */
export function MenuPerfil() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [prefsDiscreto, setPrefsDiscreto] = useState(false);
  // val4 (29/10): "Simular usuário de teste" (só com `admin.simular_usuario` e fora de uma simulação).
  const [simular, setSimular] = useState(false);
  const discreto = useDiscreto();
  const raiz = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();
  const fechar = useCallback(() => setAberto(false), []);
  const gatilho = useCallback(() => raiz.current?.querySelector<HTMLElement>(':scope > button'), []);
  const { aoTeclar } = useCamadaFlutuante({ aberto, fechar, raiz, painel, gatilho });
  // rcrm4 VIS-B3: o menu fecha antes de abrir as Preferências do modo discreto, então o Modal não tem a quem devolver o foco.
  // Ao fechá-lo, o foco volta ao botão do perfil (no efeito, depois da limpeza do Modal, que roda antes no mesmo commit).
  const voltarAoPerfil = useRef(false);
  useEffect(() => {
    if (prefsDiscreto || simular || !voltarAoPerfil.current) return;
    voltarAoPerfil.current = false;
    (gatilho() ?? raiz.current?.querySelector<HTMLElement>('button'))?.focus();
  }, [prefsDiscreto, simular, gatilho]);
  const comWorkspaces = mostrarWorkspaces(s.workspaces);
  const atualEhPadrao = !!s.workspaces.find((w) => w.workspace_id === s.workspace.id)?.padrao;
  const comEmpresas = itensSeletorEmpresa(s.empresas, '') !== null;
  const usarPadrao = async () => {
    try {
      await s.definirWorkspacePadrao(s.workspace.id);
      toast.sucesso(t('casca.padraoDefinido', { nome: s.workspace.nome }));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };
  const primeiroNome = s.usuario.nome.split(' ')[0] ?? s.usuario.nome;
  const titulo = 'px-2 pt-2 text-xs font-semibold text-tinta-3';
  const item = 'flex min-h-10 w-full min-w-0 items-center gap-2 rounded px-2 text-left text-sm text-tinta-1 hover:bg-sup-3 focus:bg-sup-3';
  return (
    <div ref={raiz} className="relative" onKeyDown={aoTeclar} data-menu-perfil="">
      <button
        type="button"
        data-tour="workspace"
        aria-label={t('casca.perfil', { nome: s.usuario.nome })}
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        onClick={() => setAberto((x) => !x)}
        className="flex h-10 min-w-0 items-center gap-2 rounded-lg px-1 text-sm font-semibold text-tinta-1 hover:bg-sup-2 sm:px-2"
      >
        <AvatarUsuario usuarioId={s.usuario.id} versao={s.usuario.avatar_versao} className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-acento text-xs font-bold text-acento-tinta" aria-hidden>
          {iniciais(s.usuario.nome)}
        </AvatarUsuario>
        <span className="hidden max-w-32 truncate xl:block" title={s.usuario.nome}>
          {primeiroNome}
        </span>
        <ChevronDown className="hidden size-4 shrink-0 text-tinta-3 sm:block" aria-hidden />
      </button>
      {aberto && (
        <div ref={painel} id={id} role="group" aria-label={t('casca.perfil', { nome: s.usuario.nome })} className="absolute right-0 z-40 mt-1 flex w-80 max-w-screen flex-col gap-1 rounded-lg border border-borda bg-sup-2 p-2 text-tinta-1 shadow-pop">
          <div className="flex min-w-0 items-center gap-3 px-2 py-2">
            <AvatarUsuario usuarioId={s.usuario.id} versao={s.usuario.avatar_versao} tamanho={256} className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-sup-3 text-sm font-semibold" aria-hidden>
              {iniciais(s.usuario.nome)}
            </AvatarUsuario>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold" title={s.usuario.nome}>{s.usuario.nome}</span>
              <span className="block truncate text-xs text-tinta-3" title={email(s.usuario.email)}>{anunciarOcultos(email(s.usuario.email))}</span>
            </span>
          </div>
          <Link to="/conta" onClick={fechar} className={item} data-minha-conta="">
            <UserRound className="size-4 shrink-0" aria-hidden />
            {t('casca.minhaConta')}
          </Link>
          {podeSimular(s) && (
            <button
              type="button"
              data-simular-item=""
              aria-haspopup="dialog"
              onClick={() => {
                setAberto(false);
                setSimular(true);
              }}
              className={item}
            >
              <UsersRound className="size-4 shrink-0" aria-hidden />
              {t('simulacao.item')}
            </button>
          )}
          {/* Val1 (28/10): quem tem um workspace só não vê nada sobre workspace; com dois ou mais, a lista agrupada por empresa
              (o tenant como título, os ambientes embaixo), com o selo "Teste" no Sandbox e o "padrão do login". */}
          {comWorkspaces && (
            <>
              <div className="border-t border-borda" aria-hidden />
              <p className={titulo}>{t('casca.workspaces')}</p>
              <div className="flex flex-col gap-1" data-perfil-workspaces="">
                {agruparWorkspaces(s.workspaces).map((g) => (
                  <div key={g.tenant_id} role="group" aria-label={g.tenant_nome} data-perfil-empresa={g.tenant_nome}>
                    <p className="truncate px-2 pt-1 text-xs font-medium text-tinta-2" title={g.tenant_nome}>
                      {g.tenant_nome}
                    </p>
                    <ul className="flex flex-col gap-0.5">
                      {g.itens.map((w) => {
                        const atual = w.workspace_id === s.workspace.id;
                        return (
                          <li key={w.workspace_id}>
                            <button
                              type="button"
                              aria-current={atual || undefined}
                              onClick={() => {
                                if (!atual) void s.trocarWorkspace(w.workspace_id);
                              }}
                              className={`${item} ${atual ? 'font-semibold text-acento-forte' : ''}`}
                            >
                              <Check className={`size-4 shrink-0 ${atual ? '' : 'invisible'}`} aria-hidden />
                              <span className="min-w-0 flex-1 truncate" title={`${g.tenant_nome} / ${w.workspace_nome}`}>
                                {w.workspace_nome}
                              </span>
                              {w.sandbox && <span className="shrink-0 rounded border border-borda px-1.5 text-xs font-medium text-tinta-2" data-selo-teste="">{t('casca.teste')}</span>}
                              {w.padrao && <span className="shrink-0 rounded bg-sup-3 px-1.5 text-xs font-medium text-tinta-2">{t('casca.padrao')}</span>}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
              {!atualEhPadrao && (
                <button type="button" onClick={() => void usarPadrao()} className={`${item} text-tinta-2`} data-usar-padrao="">
                  <span className="size-4 shrink-0" aria-hidden />
                  {t('casca.usarPadrao')}
                </button>
              )}
            </>
          )}
          {comEmpresas && (
            <>
              <div className="border-t border-borda" aria-hidden />
              <p className={titulo}>{t('casca.empresa')}</p>
              <div className="px-2 pb-1">
                <SeletorEmpresa className="w-full min-w-0" />
              </div>
            </>
          )}
          <div className="border-t border-borda" aria-hidden />
          <p className={titulo}>{t('casca.idioma')}</p>
          <div className="flex gap-1 px-2 pb-1" role="group" aria-label={t('casca.idioma')}>
            {IDIOMAS.map((i) => (
              <button
                key={i}
                type="button"
                aria-pressed={s.usuario.idioma === i}
                onClick={() => void s.preferencias({ idioma: i })}
                className={`min-h-10 flex-1 rounded border-2 px-2 text-xs font-medium hover:bg-sup-3 ${s.usuario.idioma === i ? 'border-acento-forte text-acento-forte' : 'border-transparent text-tinta-2'}`}
              >
                {t(`shell.idiomas.${i}`)}
              </button>
            ))}
          </div>
          <div className="border-t border-borda" aria-hidden />
          {/* N3 (28/10): o modo discreto (olho e preferências) saiu da barra superior e mora aqui, logo abaixo do idioma. */}
          <button
            type="button"
            data-discreto-botao=""
            aria-pressed={discreto.ativo}
            aria-keyshortcuts={ATALHO_DISCRETO}
            title={t('discreto.dica', { acao: discreto.ativo ? t('discreto.mostrar') : t('discreto.ocultar'), atalho: ATALHO_DISCRETO })}
            onClick={() => alternarDiscreto()}
            className={`${item} ${discreto.ativo ? 'font-semibold text-acento-forte' : ''}`}
          >
            {discreto.ativo ? <EyeOff className="size-4 shrink-0" aria-hidden /> : <Eye className="size-4 shrink-0" aria-hidden />}
            <span className="min-w-0 flex-1">{t('discreto.ocultar')}</span>
            {/* rcrm4 VIS-B2: o estado ligado também no texto, não só no desenho do olho (regra r6, como o check do workspace). */}
            {discreto.ativo && (
              <span className="shrink-0 rounded border border-acento-forte px-1.5 text-xs font-medium text-acento-forte" data-discreto-ligado="">
                {t('discreto.estadoLigado')}
              </span>
            )}
          </button>
          <button
            type="button"
            data-discreto-prefs=""
            aria-haspopup="dialog"
            onClick={() => {
              setAberto(false);
              setPrefsDiscreto(true);
            }}
            className={item}
          >
            <Settings2 className="size-4 shrink-0" aria-hidden />
            {t('discreto.preferencias')}
          </button>
        </div>
      )}
      <ModalSimularUsuario
        aberto={simular}
        aoFechar={() => {
          setSimular(false);
          voltarAoPerfil.current = true;
        }}
      />
      <ModalPreferenciasDiscreto
        aberto={prefsDiscreto}
        aoFechar={() => {
          setPrefsDiscreto(false);
          voltarAoPerfil.current = true;
        }}
      />
    </div>
  );
}

/**
 * Lista de 30/10 B (item 2 da ficha `2026-10-30_malgo_FUN_principal-clone-e-tela-dividida.md`): a tela dividida fixa na barra
 * superior, à esquerda da engrenagem. Liga (a tela atual abre no painel 2, com o mesmo sub-sistema) e desliga (fica o painel 1).
 * Só a partir de 1024 px, onde os painéis ficam lado a lado e a barra tem lugar (abaixo disso o atalho e o "Abrir ao lado"
 * seguem abrindo as abas do painel).
 */
function BotaoTelaDividida() {
  const { t } = useTranslation();
  const d = useTelaDividida();
  if (!d?.disponivel) return null;
  return (
    <button
      type="button"
      aria-label={t('divisao.botaoBarra')}
      aria-pressed={d.ativo}
      title={d.ativo ? t('divisao.dicaDesligar') : t('divisao.dicaLigar')}
      onClick={d.alternar}
      className={`${d.ativo ? CLASSE_ICONE_LIGADO : CLASSE_ICONE} hidden lg:flex`}
      data-tela-dividida-barra=""
    >
      <Columns2 className="size-5" aria-hidden />
    </button>
  );
}

/** N3: barra superior (64 px) na superfície da marca, sobre a barra lateral, como no clone. */
function BarraSuperiorNova({ gaveta, aoAlternarGaveta, botaoMenu }: { gaveta: boolean; aoAlternarGaveta: () => void; botaoMenu: RefObject<HTMLButtonElement | null> }) {
  const { t } = useTranslation();
  const s = useSessao();
  const sub = useSubsistema();
  const acesso = useAcessoNavegacao();
  const local = useLocation();
  const cofre = linkDoCofre(sub.links);
  const admin = destinoDoMenu('admin', acesso);
  // Lista de 30/10 (item 12): no CRM a engrenagem leva às Configurações do CRM (quando a página está no menu de quem abre); nos
  // outros sub-sistemas (ou sem ela), ao Admin, como sempre.
  const configSub = paginasDoSubsistema(s, sub.atual, modulosWeb, sub.config).find(({ slug, pagina }) => sub.atual === 'crm' && slug === 'pipeline' && pagina.chave === 'configuracoes');
  const configuracoes = configSub ? `/${configSub.pagina.caminho}` : admin;
  const naNotificacao = local.pathname === CAMINHO_NOTIFICACOES || local.pathname.startsWith(`${CAMINHO_NOTIFICACOES}/`);
  const [sino, setSino] = useState(false);
  const botaoSino = useRef<HTMLButtonElement>(null);
  return (
    <header data-sem-impressao data-barra-superior="nova" aria-label={t('casca.barra')} className="superficie-marca topo-faixa sticky z-30 flex h-16 shrink-0 items-center gap-1 bg-fundo px-2 text-tinta-1 sm:gap-2 sm:px-4">
      <button
        ref={botaoMenu}
        type="button"
        aria-expanded={gaveta}
        aria-label={gaveta ? t('comum.recolherMenu') : t('comum.abrirMenu')}
        onClick={aoAlternarGaveta}
        className={`${CLASSE_ICONE} lg:hidden`}
      >
        {gaveta ? <X className="size-5" aria-hidden /> : <IconeMenu className="size-5" aria-hidden />}
      </button>
      {/* Lote 6: no celular a marca sai para caber a lupa e o Copiloto (o menu e o seletor seguem). */}
      <Link to="/" className="hidden min-w-0 shrink items-center gap-3 rounded-lg px-1 sm:flex" data-marca-casca="">
        <MarcaDoMenu cabecalho />
      </Link>
      <span className="hidden h-6 w-0.5 shrink-0 bg-borda sm:block" aria-hidden />
      <SeletorSubsistema />
      {/* Lote 6 (N8): a busca no centro da barra, como no clone; no celular, a lupa abre o campo sob a barra. */}
      {/* Lote 6 (N8): a busca no centro da barra, como no clone; ajustes 27/10 (K12): o "Malgo IA" (Copiloto, Ajuda desta
          tela e Registro rápido num painel só) logo ao lado dela, como o farol do clone. */}
      <div className="ml-auto flex shrink-0 items-center justify-end gap-1 md:ml-0 md:min-w-0 md:flex-1 md:shrink md:justify-center md:px-4">
        <BuscaGlobal />
        <BotaoMalgoIA classe={CLASSE_ICONE} />
      </div>
      {cofre && (
        <Link to={caminhoExterno(cofre.codigo)} className={`${CLASSE_ICONE} hidden text-acento-forte lg:flex`} title={t('casca.cofre')} aria-label={t('casca.cofre')} data-cofre="">
          <KeyRound className="size-5" aria-hidden />
        </Link>
      )}
      {/* Lote 0 (complemento 7 do clone): o sino abre o pop-up só com as não lidas; a central fica em "Ver todas". */}
      <button
        ref={botaoSino}
        type="button"
        className={`${CLASSE_ICONE} relative`}
        title={t('notificacoes.sino')}
        aria-label={t('notificacoes.sino')}
        aria-haspopup="dialog"
        aria-expanded={sino}
        aria-current={naNotificacao ? 'page' : undefined}
        data-item-menu="notificacoes"
        onClick={() => setSino(true)}
      >
        <Bell className="size-5" aria-hidden />
        <SeloNotificacoes noBotao />
      </button>
      <PopupNotificacoes
        aberto={sino}
        aoFechar={() => {
          setSino(false);
          botaoSino.current?.focus();
        }}
      />
      <BotaoTelaDividida />
      {configuracoes && (
        <Link
          to={configuracoes}
          className={`${CLASSE_ICONE} hidden text-acento-forte sm:flex`}
          title={configSub ? t('casca.configuracoesCrm') : t('casca.configuracoes')}
          aria-label={configSub ? t('casca.configuracoesCrm') : t('casca.configuracoes')}
          data-tour="menu-admin"
          data-configuracoes={configSub ? 'crm' : ''}
        >
          <Settings className="size-5" aria-hidden />
        </Link>
      )}
      <div className="border-l border-borda pl-1 sm:ml-2 sm:pl-3">
        <MenuPerfil />
      </div>
      {/* Lista de 30/10 (item 4): o Sair fica só aqui (saiu do menu do perfil), então aparece em todas as larguras.
          RL301-VIS-M1: abaixo de sm cabe a 360 e 390 px porque a seta do perfil some e o espaçamento da barra encolhe. */}
      <button type="button" onClick={() => s.sair()} disabled={s.saindo} aria-busy={s.saindo} aria-label={t('comum.sair')} title={t('comum.sair')} className={`${CLASSE_ICONE} disabled:opacity-50 sm:border-l sm:border-borda`} data-sair="">
        <LogOut className="size-5" aria-hidden />
      </button>
    </header>
  );
}

/**
 * N4 e N6: barra lateral só com as páginas do sub-sistema (o Início e os menus com alguma página nele), ícone e nome; o
 * Comercial atual aparece como "Comercial 2" (X1). No rodapé, o modo escuro e o recolher/expandir (desktop).
 */
function BarraLateralNova({ compacto, aoNavegar, recolher }: { compacto?: boolean; aoNavegar?: () => void; recolher?: { recolhido: boolean; aoAlternar: () => void } }) {
  const { t } = useTranslation();
  const s = useSessao();
  const acesso = useAcessoNavegacao();
  const doMenu = useModulosDoMenu();
  const local = useLocation();
  const divisao = useMenuDaDivisao(local.pathname, aoNavegar);
  const sub = useSubsistema();
  // CRM no novo desenho (Fase 2): as páginas próprias do sub-sistema (Empresas, Contatos, Reuniões...) logo depois do Início.
  const paginas = paginasDoSubsistema(s, sub.atual, modulosWeb, sub.config);
  // Lote crm8 (item 5): o menu que uma página visível substitui sai da barra (Agenda, onde há Reuniões/Agenda).
  const substituidos = new Set(paginas.map(({ pagina }) => pagina.substituiMenu).filter(Boolean));
  const itens = MENUS.filter((m) => m.chave === 'inicio' || (doMenu(m.chave).length > 0 && !substituidos.has(m.chave)));
  const escuro = s.usuario.tema === 'escuro';
  const classeItem = (ativo: boolean) =>
    `relative flex min-h-12 items-center gap-3 border-r-4 text-sm transition-colors ${compacto ? 'justify-center px-0' : 'px-5'} ${ativo ? 'border-acento-forte font-semibold text-acento-forte' : 'border-transparent font-medium text-tinta-2 hover:bg-sup-2 hover:text-tinta-1'}`;
  // N4 (28/10): o rodapé (modo escuro e recolher) com fonte e ícones menores; a altura mínima mantém o alvo de toque.
  const classeRodape = (ativo: boolean) => `${classeItem(ativo)} !min-h-11 !text-xs`;
  const rotuloRecolher = recolher?.recolhido ? t('barra.expandirMenu') : t('barra.recolherMenu');
  return (
    <nav id={recolher ? 'menu-lateral' : undefined} aria-label={t('comum.menu')} className="flex h-full flex-col py-4">
      <ul className="flex flex-1 flex-col overflow-y-auto" data-tour="sidebar" onClickCapture={divisao.aoClicar} onContextMenu={divisao.aoContexto}>
        {itens.map((m, idx) => {
          const Icone = m.icone;
          const destino = m.chave === 'inicio' ? '/' : (destinoDoMenu(m.chave, acesso) ?? `/${m.chave}`);
          const ativo = m.chave === 'inicio' ? divisao.pathname === '/' : divisao.pathname === destino || divisao.pathname.startsWith(`/${m.chave}/`);
          const rotulo = t(m.chave === 'comercial' ? 'menus.comercial2' : `menus.${m.chave}`);
          return (
            <Fragment key={m.chave}>
              <li data-tour={idx === 1 ? 'primeiro-modulo' : undefined}>
                <Link to={destino} onClick={aoNavegar} aria-current={ativo ? 'page' : undefined} data-item-menu={m.chave} title={compacto ? rotulo : undefined} className={classeItem(ativo)}>
                  <Icone className="size-5 shrink-0" aria-hidden />
                  <span className={compacto ? 'sr-only' : 'min-w-0 flex-1 truncate'} title={rotulo}>
                    {rotulo}
                  </span>
                  {doMenu(m.chave).map((mod) => mod.seloMenu && <mod.seloMenu key={mod.slug} compacto={compacto} />)}
                </Link>
              </li>
              {m.chave === 'inicio' &&
                paginas.map(({ slug, pagina }) => {
                  const IconePagina = pagina.icone;
                  const SeloPagina = pagina.selo;
                  const para = `/${pagina.caminho}`;
                  const rotuloPagina = t(`${slug}:casca.${pagina.chave}`);
                  const ativa = divisao.pathname === para || divisao.pathname.startsWith(`${para}/`);
                  return (
                    <li key={`${slug}.${pagina.chave}`}>
                      <Link to={para} onClick={aoNavegar} aria-current={ativa ? 'page' : undefined} data-pagina-casca={pagina.chave} title={compacto ? rotuloPagina : undefined} className={classeItem(ativa)}>
                        <IconePagina className="size-5 shrink-0" aria-hidden />
                        <span className={compacto ? 'sr-only' : 'min-w-0 flex-1 truncate'} title={rotuloPagina}>
                          {rotuloPagina}
                        </span>
                        {SeloPagina && <SeloPagina compacto={compacto} />}
                      </Link>
                    </li>
                  );
                })}
            </Fragment>
          );
        })}
      </ul>
      {divisao.menu}
      <div className="flex flex-col" data-rodape-menu="">
        <button
          type="button"
          aria-label={t('tema.escuro')}
          aria-pressed={escuro}
          title={escuro ? t('casca.modoClaro') : t('casca.modoEscuro')}
          onClick={() => s.preferencias({ tema: escuro ? 'claro' : 'escuro' })}
          className={classeRodape(false)}
        >
          {escuro ? <Sun className="size-3.5 shrink-0" aria-hidden /> : <Moon className="size-3.5 shrink-0" aria-hidden />}
          <span className={compacto ? 'sr-only' : 'min-w-0 flex-1 truncate text-left'} title={escuro ? t('casca.modoClaro') : t('casca.modoEscuro')}>
            {escuro ? t('casca.modoClaro') : t('casca.modoEscuro')}
          </span>
        </button>
        {recolher && (
          <button type="button" data-recolher-menu aria-expanded={!recolher.recolhido} aria-controls="menu-lateral" aria-label={rotuloRecolher} title={rotuloRecolher} onClick={recolher.aoAlternar} className={classeRodape(false)}>
            {recolher.recolhido ? <PanelLeftOpen className="size-3.5 shrink-0" aria-hidden /> : <PanelLeftClose className="size-3.5 shrink-0" aria-hidden />}
            <span className={compacto ? 'sr-only' : 'min-w-0 flex-1 truncate text-left'} title={rotuloRecolher}>
              {rotuloRecolher}
            </span>
          </button>
        )}
      </div>
    </nav>
  );
}

/** A casca do novo desenho (mesmos provedores, tela dividida, tour e vigias da casca de sempre). */
export function CascaNova({ rotas }: { rotas?: () => ReactNode }) {
  const { t } = useTranslation();
  const s = useSessao();
  const sub = useSubsistema();
  // Lista de 30/10 B (tela dividida): cada painel com o seu sub-sistema. O do painel 1 é o da aba; o do 2 fica na divisão.
  const divisao = useEstadoTelaDividida(s.usuario.id, !!rotas, { principal: sub.atual, escolherPrincipal: sub.escolher });
  const { ativo: divisaoAtiva, subLado, definirSubLado } = divisao;
  useEffect(() => {
    if (divisaoAtiva && !subLado && sub.atual) definirSubLado(sub.atual);
  }, [divisaoAtiva, subLado, sub.atual, definirSubLado]);
  const subDoLado = useMemo(() => (subLado ? comSubsistema(sub, subLado, definirSubLado) : null), [sub, subLado, definirSubLado]);
  // A barra superior, o menu lateral e o botão flutuante seguem o painel ativo.
  const subAtivo = divisao.foco === 'lado' && subDoLado ? subDoLado : sub;
  const envolverLado = useCallback((n: ReactNode) => (subDoLado ? <ProvedorSubsistemaDoPainel valor={subDoLado}>{n}</ProvedorSubsistemaDoPainel> : n), [subDoLado]);
  const [gaveta, setGaveta] = useState(false);
  const local = useLocation();
  const [tour, setTour] = useState(false);
  const [recolhido, alternarMenu] = useMenuRecolhido(s.usuario.id);
  const compacto = recolhido && !tour;
  useEffect(() => setGaveta(false), [local.pathname]);
  const fecharGaveta = useCallback(() => setGaveta(false), []);
  const gavetaInterativa = gaveta && !tour;
  useEsc(gavetaInterativa, fecharGaveta);
  const refGaveta = useFocoInicial(gavetaInterativa);
  const botaoMenu = useRef<HTMLButtonElement>(null);
  const abriuGaveta = useRef(false);
  useEffect(() => {
    if (gavetaInterativa) abriuGaveta.current = true;
    else if (!gaveta && abriuGaveta.current) {
      abriuGaveta.current = false;
      botaoMenu.current?.focus();
    }
  }, [gaveta, gavetaInterativa]);
  // Lista de 30/10 (item 3): a partir de 1024 px só a área central rola. Se algo rolar a janela (foco ou `scrollIntoView` de um
  // elemento fora da vista), ela volta ao topo: senão a casca sobe por baixo da barra e some o canto arredondado do conteúdo.
  useEffect(() => {
    const larga = window.matchMedia('(min-width: 64rem)');
    const aoRolar = () => {
      if (larga.matches && (window.scrollX || window.scrollY)) window.scrollTo(0, 0);
    };
    window.addEventListener('scroll', aoRolar, { passive: true });
    return () => window.removeEventListener('scroll', aoRolar);
  }, []);
  useEffect(() => {
    if (!s.usuario.onboarding_concluido) {
      const id = setTimeout(() => setTour(true), 600);
      return () => clearTimeout(id);
    }
  }, [s.usuario.onboarding_concluido]);

  return (
    <ProvedorTelaDividida value={divisao}>
      <ProvedorAcaoPrimaria>
        <PularParaConteudo />
        <FaixaAmbiente />
        <div data-casca="nova" className="casca-fixa flex min-h-full flex-col lg:min-h-0">
          <ProvedorSubsistemaDoPainel valor={subAtivo}>
            <BarraSuperiorNova gaveta={gaveta} aoAlternarGaveta={() => setGaveta((x) => !x)} botaoMenu={botaoMenu} />
          </ProvedorSubsistemaDoPainel>
          <div className="relative flex min-h-0 min-w-0 flex-1">
            {/* O fundo da marca atrás do canto arredondado do conteúdo (a barra lateral e a superior parecem uma peça só). */}
            <div className="superficie-marca pointer-events-none absolute inset-0 bg-fundo" aria-hidden />
            <aside data-menu-lateral={compacto ? 'recolhido' : 'aberto'} className={`superficie-marca topo-casca altura-sob-casca sticky z-20 hidden shrink-0 bg-fundo lg:block ${compacto ? 'w-16' : 'w-56'}`}>
              <ProvedorSubsistemaDoPainel valor={subAtivo}>
                <BarraLateralNova compacto={compacto} recolher={{ recolhido, aoAlternar: alternarMenu }} />
              </ProvedorSubsistemaDoPainel>
            </aside>
            {gaveta && (
              <div className="topo-faixa fixed inset-x-0 bottom-0 z-40 bg-veu lg:hidden" inert={tour} onClick={() => setGaveta(false)}>
                <div ref={refGaveta} data-gaveta data-sem-impressao role="dialog" aria-modal={!tour} aria-label={t('comum.menu')} tabIndex={-1} className="superficie-marca relative flex h-full w-64 max-w-full flex-col bg-fundo focus:outline-none" onClick={(e) => e.stopPropagation()}>
                  <button type="button" aria-label={t('comum.recolherMenu')} onClick={() => setGaveta(false)} className="absolute top-2 right-2 z-10 flex size-11 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1">
                    <X className="size-5" aria-hidden />
                  </button>
                  <div className="flex min-h-14 shrink-0 items-center gap-2 pt-2 pr-14 pl-4" data-marca-gaveta="">
                    <MarcaDoMenu cabecalho />
                  </div>
                  <div className="min-h-0 flex-1">
                  <ProvedorSubsistemaDoPainel valor={subAtivo}>
                    <BarraLateralNova aoNavegar={() => setGaveta(false)} />
                  </ProvedorSubsistemaDoPainel>
                  </div>
                </div>
              </div>
            )}
            {/* Lote crm6 (item 8): a área que rola (`rolagem-conteudo`, a partir de 1024 px); a borda arredondada fica parada. */}
            <div data-rolagem-conteudo="" className={`rolagem-conteudo relative flex min-w-0 flex-1 flex-col bg-fundo lg:rounded-tl-2xl lg:border-t lg:border-l lg:border-borda ${divisao.ativo && !divisao.emAbas ? 'topo-casca altura-sob-casca sticky' : ''}`}>
              <AreaDoConteudo d={divisao} rotas={rotas} envolverLado={envolverLado}>
                <main id="conteudo" tabIndex={-1} data-painel-conteudo="principal" data-painel-ativo={divisao.foco === 'principal' ? '' : undefined} aria-label={divisao.ativo ? t('divisao.painel', { n: 1 }) : undefined} className="mx-auto w-full max-w-7xl min-w-0 flex-1 px-4 py-6 focus:outline-none sm:px-6 lg:px-8">
                  <LimiteErro key={local.pathname.split('/').slice(0, 3).join('/')}>
                    <Outlet />
                  </LimiteErro>
                </main>
              </AreaDoConteudo>
            </div>
          </div>
        </div>
        {/* Ajustes 27/10 (C6): o botão flutuante "Criar novo..." nas páginas do sub-sistema (as criações dos módulos dele). */}
        <BotaoFlutuanteCriar subsistema={subAtivo.atual} />
        <VigiaDiscreto />
        <VigiaNotificacoes />
        {tour && (
          <Tour
            menuAberto={gaveta}
            definirMenu={setGaveta}
            aoConcluir={() => {
              setTour(false);
              void s.preferencias({ onboarding_concluido: true });
            }}
          />
        )}
      </ProvedorAcaoPrimaria>
    </ProvedorTelaDividida>
  );
}
